import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import debounce from 'lodash.debounce'
import styled from 'styled-components'
import { rem } from 'polished'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalBody from 'LuxKit/Modal/ModalBody'
import UserReviewTile from './UserReviewTile'
import useUserReviews from 'hooks/Reviews/useUserReviews'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import NumberRating from 'components/Common/NumberRating/NumberRating'
import RatingDistribution from 'components/Common/RatingDistribution'
import RatingBreakdownItem from 'components/Common/RatingBreakdownItem'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import LineThumbsUpIcon from 'LuxKit/Icons/line/LineThumbsUpIcon'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Divider from 'LuxKit/Divider'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { isOfferRatingDisplayable } from 'lib/order/reviewUtils'
import Group from '../../utils/Group'
import CSSBreakpoint from '../../utils/CSSBreakpoint'
import type { ReviewCompanionshipType } from 'api/review'
import useAutoUpdatingRef from 'hooks/useAutoUpdatingRef'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import {
  mapRatingDistribution,
  mapRatingBreakdown,
  getRecommendPercentageText,
} from 'lib/reviews/ratingBreakdownUtils'
import ReviewFilterBar from './ReviewFilterBar'
import type { ReviewPanelSortType } from 'constants/review'
import {
  RATING_RANGE_TO_FILTER_MAP,
} from 'constants/review'

// Reviews panel state type and reducer
interface ReviewsPanelState {
  loadedReviews: Array<App.Review>
  currentPage: number
  searchInput: string
  activeSearch: string
  sort: ReviewPanelSortType
  ratingFilter: string
  travellerFilter: ReviewCompanionshipType | ''
}

type ReviewsPanelAction =
  | { type: 'SET_LOADED_REVIEWS'; payload: Array<App.Review> }
  | { type: 'SET_CURRENT_PAGE'; payload: number }
  | { type: 'INCREMENT_PAGE' }
  | { type: 'SET_SEARCH_INPUT'; payload: string }
  | { type: 'SET_SORT'; payload: ReviewPanelSortType }
  | { type: 'SET_RATING_FILTER'; payload: string }
  | { type: 'SET_TRAVELLER_FILTER'; payload: ReviewCompanionshipType | '' }
  | { type: 'SUBMIT_SEARCH' }
  | { type: 'RESET_PAGINATION' }
  | { type: 'RESET'; payload?: ReviewPanelSortType }

const DEFAULT_SORT: ReviewPanelSortType = 'newest'

function getInitialState(initialSort?: ReviewPanelSortType): ReviewsPanelState {
  return {
    loadedReviews: [],
    currentPage: 1,
    searchInput: '',
    activeSearch: '',
    sort: initialSort ?? DEFAULT_SORT,
    ratingFilter: '',
    travellerFilter: '',
  }
}

function reviewsPanelReducer(state: ReviewsPanelState, action: ReviewsPanelAction): ReviewsPanelState {
  switch (action.type) {
    case 'SET_LOADED_REVIEWS':
      return { ...state, loadedReviews: action.payload }
    case 'SET_CURRENT_PAGE':
      return { ...state, currentPage: action.payload }
    case 'INCREMENT_PAGE':
      return { ...state, currentPage: state.currentPage + 1 }
    case 'SET_SEARCH_INPUT':
      return { ...state, searchInput: action.payload }
    case 'SET_SORT':
      return { ...state, sort: action.payload }
    case 'SET_RATING_FILTER':
      return { ...state, ratingFilter: action.payload }
    case 'SET_TRAVELLER_FILTER':
      return { ...state, travellerFilter: action.payload }
    case 'SUBMIT_SEARCH':
      return { ...state, activeSearch: state.searchInput, currentPage: 1, loadedReviews: [] }
    case 'RESET_PAGINATION':
      return { ...state, currentPage: 1, loadedReviews: [] }
    case 'RESET':
      return getInitialState(action.payload)
    default:
      return state
  }
}

const PanelGrid = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;

  ${mediaQueryUp.desktop} {
    display: grid;
    grid-template-columns: ${rem(280)} 1fr;
    grid-template-rows: 1fr;
    column-gap: ${rem(24)};
    overflow-y: hidden;
  }
`

const Sidebar = styled.div`
  padding: ${rem(20)};

  ${mediaQueryUp.desktop} {
    border-bottom: none;
    overflow-y: auto;
  }
`

const RatingBreakdownSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  row-gap: ${rem(16)};
  column-gap: ${rem(24)};
  justify-items: center;
`

const RatingBreakdownSectionMobile = styled(Group)`
  padding: 0 ${rem(8)};
`

const ReviewsColumn = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  min-height: 0;

  ${mediaQueryUp.desktop} {
    overflow-y: hidden;
  }
`

const FilterBar = styled.div`
  padding: ${rem(16)} ${rem(20)};
  border-bottom: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
`

const ReviewsList = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 ${rem(8)} ${rem(20)};
  position: relative;
  flex: 1;
  min-height: 0;

  ${mediaQueryUp.desktop} {
    overflow-y: auto;
  }
`

const EmptyState = styled.div`
  padding: ${rem(40)} ${rem(20)};
  text-align: center;
`

const SearchResultInfo = styled.div`
  padding: ${rem(12)} ${rem(20)};
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
`

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: ${rem(24)} 0;
`

const PAGE_SIZE = 9

interface Props {
  isOpen: boolean
  onClose: () => void
  property?: App.OfferProperty | App.BedbankProperty
  isLPP?: boolean
  experience?: App.ExperienceOffer
  tour?: App.Tours.TourV2Offer
  initialRatingFilter?: string
  initialReviewId?: string
  initialSort?: ReviewPanelSortType
}

function ReviewsPanel({
  isOpen,
  onClose,
  property,
  isLPP,
  experience,
  tour,
  initialRatingFilter = '',
  initialReviewId = '',
  initialSort,
}: Props) {
  const intl = useIntl()

  // Track the trigger element - using state so we re-run effects when it mounts
  const [triggerElement, setTriggerElement] = useState<HTMLDivElement | null>(null)

  // Reviews panel state with useReducer - initialize with initialSort to avoid flash
  const [state, dispatch] = useReducer(reviewsPanelReducer, getInitialState(initialSort))
  const {
    loadedReviews,
    currentPage,
    searchInput,
    activeSearch,
    sort,
    ratingFilter,
    travellerFilter,
  } = state

  const isDesktopUp = useScreenSizeUp('desktop')

  // Track which review ID we're currently loading pages for (prevents infinite loop)
  const loadingForReviewIdRef = useRef<string>('')

  const offset = (currentPage - 1) * PAGE_SIZE
  const [reviewsData, fetchingReviews] = useUserReviews({
    propertyProvider: isLPP ? 'bedbank' : 'luxuryescapes',
    propertyId: property?.id,
    experienceId: experience?.id,
    tourId: tour?.id,
    useExternalReviews: true,
    useHiddenForProperty: true,
    limit: PAGE_SIZE,
    offset,
    sort,
    ratings: ratingFilter ? [ratingFilter] : undefined,
    companionship: travellerFilter ? [travellerFilter] : undefined,
    search: activeSearch || undefined,
  })

  // Combine previously loaded reviews with current page
  const allReviews = useMemo(() => {
    return currentPage === 1
      ? (reviewsData?.reviews ?? [])
      : [...loadedReviews, ...(reviewsData?.reviews ?? [])]
  }, [currentPage, reviewsData?.reviews, loadedReviews])

  const hasMoreReviews = allReviews.length < (reviewsData?.total ?? 0)

  const onLoadMore = useCallback(() => {
    if (!fetchingReviews && hasMoreReviews) {
      dispatch({ type: 'SET_LOADED_REVIEWS', payload: allReviews })
      dispatch({ type: 'INCREMENT_PAGE' })
    }
  }, [allReviews, fetchingReviews, hasMoreReviews])

  // Keep latest onLoadMore in a ref so debounced function always calls current version
  const onLoadMoreRef = useAutoUpdatingRef(onLoadMore)

  // Debounced version of onLoadMore - stable function that won't reset timer
  const debouncedLoadMore = useMemo(
    () => debounce(() => onLoadMoreRef.current(), 200),
    [onLoadMoreRef],
  )

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedLoadMore.cancel()
    }
  }, [debouncedLoadMore])

  // Reset state when panel closes
  useEffect(() => {
    if (!isOpen) {
      dispatch({ type: 'RESET', payload: initialSort })
    }
  }, [isOpen, initialSort])

  // Set initial rating filter when panel opens
  useEffect(() => {
    if (isOpen && initialRatingFilter) {
      dispatch({ type: 'SET_RATING_FILTER', payload: initialRatingFilter })
    }
  }, [isOpen, initialRatingFilter])

  // Scroll to specific review when panel opens with initialReviewId
  useEffect(() => {
    if (isOpen && initialReviewId && !fetchingReviews && allReviews.length > 0) {
      // Check if the review exists in loaded reviews
      const reviewExists = allReviews.some(r => r.id === initialReviewId)

      if (reviewExists) {
        // Reset the loading tracker
        loadingForReviewIdRef.current = ''

        setTimeout(() => {
          const reviewElement = document.querySelector(`[data-review-id="${initialReviewId}"]`)
          if (reviewElement) {
            reviewElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
          }
        }, 100)
      } else if (hasMoreReviews && loadingForReviewIdRef.current !== initialReviewId) {
        // Only start loading if we haven't already for this review ID
        loadingForReviewIdRef.current = initialReviewId
        onLoadMoreRef.current()
      }
    }

    // Reset when panel closes
    if (!isOpen) {
      loadingForReviewIdRef.current = ''
    }
  }, [isOpen, initialReviewId, fetchingReviews, allReviews, hasMoreReviews, onLoadMoreRef])

  // Reset pagination when filters change
  useEffect(() => {
    dispatch({ type: 'RESET_PAGINATION' })
  }, [sort, ratingFilter, travellerFilter])

  // Infinite scroll using Intersection Observer
  // Using triggerElement (state) instead of a ref so effect re-runs when element mounts
  useEffect(() => {
    if (!triggerElement || !isOpen) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !fetchingReviews && hasMoreReviews) {
          debouncedLoadMore()
        }
      },
      { threshold: 0.1 },
    )

    observer.observe(triggerElement)

    return () => {
      observer.disconnect()
    }
  }, [triggerElement, isOpen, fetchingReviews, hasMoreReviews, debouncedLoadMore])

  const handleClose = useCallback(() => {
    onClose()
  }, [onClose])

  // Search handlers
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch({ type: 'SET_SEARCH_INPUT', payload: e.target.value })
  }, [])

  const handleSearchSubmit = useCallback(() => {
    dispatch({ type: 'SUBMIT_SEARCH' })
  }, [])

  // Sort handler
  const handleSortChange = useCallback((value: ReviewPanelSortType) => {
    dispatch({ type: 'SET_SORT', payload: value })
  }, [])

  // Rating filter handler
  const handleRatingChange = useCallback((value: string) => {
    dispatch({ type: 'SET_RATING_FILTER', payload: value })
  }, [])

  // Rating distribution click handler
  const handleRatingDistributionClick = useCallback((ratingRange: string) => {
    const filter = RATING_RANGE_TO_FILTER_MAP[ratingRange] || ''
    dispatch({ type: 'SET_RATING_FILTER', payload: filter })
  }, [])

  // Traveller filter handler
  const handleTravellerChange = useCallback((value: ReviewCompanionshipType | '') => {
    dispatch({ type: 'SET_TRAVELLER_FILTER', payload: value })
  }, [])

  const displayableRating = property?.rating ?? experience?.rating ?? tour?.rating
  const isGoogleReviews = displayableRating?.reviewsSource === 'google'
  const offerRatingDisplayable = isOfferRatingDisplayable(displayableRating)

  // Extract rating breakdown data from property, experience, or tour
  const ratingBreakdown =
    property?.rating?.reviewsRatingBreakdown ??
    experience?.rating?.reviewsRatingBreakdown ??
    tour?.rating?.reviewsRatingBreakdown

  // Map rating distribution from backend format to frontend format
  const ratingDistributions = useMemo(
    () => mapRatingDistribution(ratingBreakdown?.ratingDistribution, intl),
    [ratingBreakdown?.ratingDistribution, intl],
  )

  // Map rating breakdown items from backend format to frontend format
  const ratingBreakdowns = useMemo(
    () => mapRatingBreakdown(ratingBreakdown, intl),
    [ratingBreakdown, intl],
  )

  // Get recommend percentage text
  const entityType = property ? 'hotel' : tour ? 'tour' : 'experience'
  const recommendText = useMemo(
    () => getRecommendPercentageText(ratingBreakdown?.recommendPercentage, entityType, intl),
    [ratingBreakdown?.recommendPercentage, entityType, intl],
  )

  // Translated UI labels
  const reviewsTitle = intl.formatMessage({
    id: '6DYPXp7C0OeyWCMIKwBf',
    defaultMessage: 'Reviews',
    description: 'Title for the reviews panel modal in the guest reviews section',
  })

  const reviewsFromGoogleSubtitle = intl.formatMessage({
    id: 'xMtKhagm4xgOxZQKmgP4',
    defaultMessage: 'Reviews from Google',
    description: 'Subtitle indicating reviews are sourced from Google',
  })

  const noReviewsWithFilters = intl.formatMessage({
    id: 'pcwghpRYxsEwTW0Vjksv',
    defaultMessage: 'No reviews found with the selected filters',
    description: 'Empty state message when no reviews match the selected filters',
  })

  return (
    <ModalBase
      isOpen={isOpen}
      onClose={handleClose}
      mode="drawer-starboard"
      data-reviews-panel="true"
      size={isDesktopUp ? 'XXL' : 'max'}
    >
      <ModalHeader title={reviewsTitle} onCloseButtonClick={handleClose} />
      <ModalBody>
        <PanelGrid>
          {/* Sidebar with rating summary */}
          <Sidebar>
            <VerticalSpacer gap={24}>
              {/* Number Rating */}
              {offerRatingDisplayable && (
                <NumberRating
                  variant="xxl"
                  rating={displayableRating.score}
                  total={displayableRating.reviewsTotal}
                  ratingSubtitle={isGoogleReviews ? reviewsFromGoogleSubtitle : undefined}
                />
              )}
              {!isGoogleReviews && (
                <>
                  <CSSBreakpoint min="desktop">
                    <VerticalSpacer gap={24}>
                      {/* Rating Distribution */}
                      {ratingDistributions.length > 0 && (
                        <>
                          <VerticalSpacer gap={0}>
                            {ratingDistributions.map(item => (
                              <RatingDistribution
                                key={item.id}
                                label={item.label}
                                range={item.range}
                                progress={item.progress}
                                count={item.count}
                                width={225}
                                isPanel
                                onClick={() => handleRatingDistributionClick(item.range)}
                              />
                            ))}
                          </VerticalSpacer>
                          <Divider kind="primary" direction="horizontal" />
                        </>
                      )}

                      {/* Rating Breakdown */}
                      {ratingBreakdowns.length > 0 && (
                        <>
                          <RatingBreakdownSection>
                            {ratingBreakdowns.map(item => (
                              <RatingBreakdownItem
                                key={item.id}
                                score={item.score}
                                label={item.label}
                              />
                            ))}
                          </RatingBreakdownSection>
                          <Divider kind="primary" direction="horizontal" />
                        </>
                      )}
                    </VerticalSpacer>
                  </CSSBreakpoint>
                  <CSSBreakpoint max="tablet">
                    {/* Rating Distribution */}
                    {(ratingDistributions.length > 0 || ratingBreakdowns.length > 0) && (
                      <VerticalSpacer gap={16}>
                        {/* Rating Distribution */}
                        {ratingDistributions.length > 0 && (
                          <Group direction="vertical" verticalAlign="space-between" gap={4}>
                            {ratingDistributions.map(item => (
                              <RatingDistribution
                                key={item.id}
                                label={item.label}
                                range={item.range}
                                progress={item.progress}
                                count={item.count}
                                onClick={() => handleRatingDistributionClick(item.range)}
                              />
                            ))}
                          </Group>
                        )}
                        {/* Rating Breakdown */}
                        {ratingBreakdowns.length > 0 && (
                          <RatingBreakdownSectionMobile direction="horizontal" horizontalAlign="space-between">
                            {ratingBreakdowns.map(item => (
                              <RatingBreakdownItem
                                key={item.id}
                                score={item.score}
                                label={item.label}
                              />
                            ))}
                          </RatingBreakdownSectionMobile>
                        )}
                      </VerticalSpacer>
                    )}
                  </CSSBreakpoint>

                  {/* Recommendation */}
                  {recommendText && (
                    <BodyTextBlock
                      weight="bold"
                      variant="medium"
                      startIcon={<LineThumbsUpIcon colour="highlight-primary-dark" />}
                    >
                      {recommendText}
                    </BodyTextBlock>
                  )}
                </>
              )}
            </VerticalSpacer>
          </Sidebar>

          {/* Reviews Column with Filter Bar and List */}
          <ReviewsColumn>
            {/* Filter Bar */}
            {!isGoogleReviews && (
              <FilterBar>
                <ReviewFilterBar
                  searchInput={searchInput}
                  onSearchChange={handleSearchChange}
                  onSearchSubmit={handleSearchSubmit}
                  sort={sort}
                  defaultSort={initialSort ?? DEFAULT_SORT}
                  onSortChange={handleSortChange}
                  ratingFilter={ratingFilter}
                  onRatingChange={handleRatingChange}
                  travellerFilter={travellerFilter}
                  onTravellerChange={handleTravellerChange}
                />
              </FilterBar>
            )}
            {/* Search Result Info */}
            {activeSearch && allReviews.length > 0 && (
              <SearchResultInfo>
                <BodyText variant="medium">
                  <FormattedMessage
                    id="3RVZ4sOd6iaX7Kq-jo_2"
                    defaultMessage="{count, plural, one {# review} other {# reviews}} mentioned ''<strong>{searchTerm}</strong>''"
                    description="Search results summary showing how many reviews mention the search term"
                    values={{
                      count: reviewsData?.total ?? allReviews.length,
                      searchTerm: activeSearch,
                      strong: (chunks) => <strong>{chunks}</strong>,
                    }}
                  />
                </BodyText>
              </SearchResultInfo>
            )}

            {/* Reviews List */}
            <ReviewsList>
              <LoadingIndicator floating visible={fetchingReviews && currentPage === 1} />

              {/* Empty state for no search results */}
              {activeSearch && allReviews.length === 0 && !fetchingReviews && (
                <EmptyState>
                  <BodyText variant="medium">
                    <FormattedMessage
                      id="ne1mg6dh-F4uMi7tEExK"
                      defaultMessage="There are no results for ''<strong>{searchTerm}</strong>''"
                      description="Empty state message when search returns no review results"
                      values={{
                        searchTerm: activeSearch,
                        strong: (chunks) => <strong>{chunks}</strong>,
                      }}
                    />
                  </BodyText>
                </EmptyState>
              )}

              {/* Empty state for no reviews with filters */}
              {!activeSearch && allReviews.length === 0 && !fetchingReviews && (
                <EmptyState>
                  <BodyText variant="medium">
                    {noReviewsWithFilters}
                  </BodyText>
                </EmptyState>
              )}

              {allReviews.map(review => (
                <div key={review.id} data-review-id={review.id}>
                  <UserReviewTile
                    userReview={{
                      ...review,
                      experienceId: experience?.id,
                      tourId: tour?.id,
                    }}
                    variant="panel"
                    showReviewFiltersAndUserDetails
                    defaultExpanded={initialReviewId === review.id}
                    searchTerm={activeSearch}
                  />
                </div>
              ))}

              {/* Infinite scroll trigger */}
              <div ref={setTriggerElement}>
                {fetchingReviews && currentPage > 1 && (
                  <LoadingContainer>
                    <LoadingIndicator visible />
                  </LoadingContainer>
                )}
              </div>
            </ReviewsList>
          </ReviewsColumn>
        </PanelGrid>
      </ModalBody>
    </ModalBase>
  )
}

export default React.memo(ReviewsPanel)
